import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface ProfissionalPagamentosProps {
  professionalId: string;
}

export function ProfissionalPagamentos({ professionalId }: ProfissionalPagamentosProps) {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState<any[]>([]);

  useEffect(() => {
    loadInvoices();
  }, [professionalId]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("invoices")
        .select("*")
        .eq("user_id", professionalId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setInvoices(data || []);
    } catch (error) {
      console.error("Error loading invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive"> = {
      paid: "default",
      pending: "secondary",
      canceled: "destructive",
      failed: "destructive",
    };
    const labels: Record<string, string> = {
      paid: "Pago",
      pending: "Pendente",
      canceled: "Cancelado",
      failed: "Falhou",
    };
    return (
      <Badge variant={variants[status] || "secondary"}>
        {labels[status] || status}
      </Badge>
    );
  };

  if (loading) {
    return <p>Carregando...</p>;
  }

  if (invoices.length === 0) {
    return (
      <p className="text-muted-foreground text-center py-8">
        Nenhum pagamento registrado
      </p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Data</TableHead>
          <TableHead>Vencimento</TableHead>
          <TableHead>Valor</TableHead>
          <TableHead>Método</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>NFS-e</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {invoices.map((invoice) => (
          <TableRow key={invoice.id}>
            <TableCell>
              {format(new Date(invoice.created_at), "dd/MM/yyyy", { locale: ptBR })}
            </TableCell>
            <TableCell>
              {format(new Date(invoice.due_date), "dd/MM/yyyy", { locale: ptBR })}
            </TableCell>
            <TableCell className="font-medium">
              R$ {(invoice.amount_cents / 100).toFixed(2)}
            </TableCell>
            <TableCell>{invoice.payment_method || "—"}</TableCell>
            <TableCell>{getStatusBadge(invoice.status)}</TableCell>
            <TableCell>
              {invoice.nfse_number ? (
                invoice.nfse_pdf_url ? (
                  <a
                    href={invoice.nfse_pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {invoice.nfse_number}
                  </a>
                ) : (
                  invoice.nfse_number
                )
              ) : (
                "—"
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}